import type { ApiError, ClientMessage, ServerMessage, SessionResponse } from "@racemax/shared";

export type ConnectionState = "connecting" | "connected" | "reconnecting" | "closed";

export async function createRoom(name: string): Promise<SessionResponse> {
  return postSession("/api/rooms", name);
}

export async function joinRoom(code: string, name: string): Promise<SessionResponse> {
  return postSession(`/api/rooms/${encodeURIComponent(code)}/join`, name);
}

async function postSession(url: string, name: string): Promise<SessionResponse> {
  const response = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name }),
  });
  const payload: unknown = await response.json();
  if (!response.ok) {
    const error = payload as Partial<ApiError>;
    throw new Error(error.message ?? "Impossible de rejoindre le salon.");
  }
  return payload as SessionResponse;
}

export class RoomClient {
  readonly session: SessionResponse;
  serverOffset = 0;
  onMessage: (message: ServerMessage) => void = () => undefined;
  onConnectionState: (state: ConnectionState) => void = () => undefined;

  private socket: WebSocket | null = null;
  private intentionallyClosed = false;
  private reconnectStartedAt = 0;
  private reconnectTimer: number | null = null;
  private pingTimer: number | null = null;

  constructor(session: SessionResponse) {
    this.session = session;
  }

  connect(): void {
    this.intentionallyClosed = false;
    this.openSocket(false);
  }

  send(message: ClientMessage): boolean {
    if (this.socket?.readyState !== WebSocket.OPEN) return false;
    this.socket.send(JSON.stringify(message));
    return true;
  }

  serverNow(): number {
    return Date.now() + this.serverOffset;
  }

  close(): void {
    this.intentionallyClosed = true;
    this.onConnectionState("closed");
    if (this.reconnectTimer !== null) window.clearTimeout(this.reconnectTimer);
    if (this.pingTimer !== null) window.clearInterval(this.pingTimer);
    this.reconnectTimer = null;
    this.pingTimer = null;
    this.socket?.close(1000, "Nouvelle partie");
    this.socket = null;
  }

  private openSocket(reconnecting: boolean): void {
    this.onConnectionState(reconnecting ? "reconnecting" : "connecting");
    const scheme = location.protocol === "https:" ? "wss" : "ws";
    const url = `${scheme}://${location.host}/api/rooms/${this.session.roomCode}/socket`;
    const socket = new WebSocket(url, ["racemax.v1", `player.${this.session.playerId}`, `auth.${this.session.resumeToken}`]);
    this.socket = socket;

    socket.addEventListener("open", () => {
      this.reconnectStartedAt = 0;
      this.onConnectionState("connected");
      this.sendPing();
      if (this.pingTimer !== null) window.clearInterval(this.pingTimer);
      this.pingTimer = window.setInterval(() => this.sendPing(), 3_000);
    });
    socket.addEventListener("message", (event: MessageEvent<string>) => {
      let message: ServerMessage;
      try {
        message = JSON.parse(event.data) as ServerMessage;
      } catch {
        return;
      }
      if (message.type === "pong") {
        const receivedAt = Date.now();
        const roundTrip = receivedAt - message.clientTime;
        const measuredOffset = message.serverTime + roundTrip / 2 - receivedAt;
        this.serverOffset = this.serverOffset === 0 ? measuredOffset : this.serverOffset * 0.8 + measuredOffset * 0.2;
      }
      this.onMessage(message);
    });
    socket.addEventListener("close", () => {
      if (this.pingTimer !== null) window.clearInterval(this.pingTimer);
      this.pingTimer = null;
      if (!this.intentionallyClosed) this.scheduleReconnect();
    });
    socket.addEventListener("error", () => {
      if (socket.readyState !== WebSocket.CLOSED) socket.close();
    });
  }

  private scheduleReconnect(): void {
    const now = Date.now();
    if (this.reconnectStartedAt === 0) this.reconnectStartedAt = now;
    if (now - this.reconnectStartedAt >= 29_000) {
      this.onConnectionState("closed");
      return;
    }
    this.onConnectionState("reconnecting");
    const elapsed = now - this.reconnectStartedAt;
    const delay = Math.min(3_000, 400 + elapsed / 5);
    this.reconnectTimer = window.setTimeout(() => this.openSocket(true), delay);
  }

  private sendPing(): void {
    this.send({ type: "ping", clientTime: Date.now() });
  }
}
